import React, { FC } from 'react';
import { replace } from '@aarhus-university/au-lib-react-components/src/lib/helpers';
import { useForm } from 'react-hook-form';
import { useLangContext } from '@aarhus-university/au-lib-react-components/src/lib/context';
import AUSpinnerComponent from '@aarhus-university/au-lib-react-components/src/components/AUSpinnerComponent';
import { getContextBasedHeadline } from '../../lib/helpers';
import ReceiptComponent from './ReceiptComponent';
import ErrorComponent from '../common/ErrorComponent';
import { emailRegex } from '../../lib/common';
import { useTranslationsContext } from '../../lib/translation';
import { useRegistrationContext } from '../../lib/context';
import { useUserSignupByCprMutation } from '../../services/api';

const CreateCPRComponent: FC = () => {
  const {
    register, handleSubmit, formState: { errors },
  } = useForm<IUserSignupByCprPayload>();

  const lang = useLangContext();
  const translations = useTranslationsContext();
  const context = useRegistrationContext();
  const [userSignupByCpr, response] = useUserSignupByCprMutation();
  const { data: userSignupByCprResult, error, isLoading: uiBusy } = response;
  // The CPR number could not be looked up (422); any other error status is a general failure
  const invalidCpr = error != null && 'status' in error && error.status === 422;

  const onSubmit = (data: IUserSignupByCprPayload) => {
    userSignupByCpr({
      privateEmail: data.privateEmail,
      preferredLanguage: 'en',
      context: context?.key as string,
      culture: lang,
    });
  };

  if (!context) {
    return null;
  }

  const {
    key,
  } = context;

  let disclaimer = translations.disclaimerDefaultText;
  const { disclaimerDa, disclaimerEn } = context as ISignupContext;
  if (lang === 'da' && disclaimerDa !== null) {
    disclaimer = disclaimerDa;
  } else if (lang === 'en' && disclaimerEn !== null) {
    disclaimer = disclaimerEn;
  }

  if (!userSignupByCprResult) {
    return (
      <div className="page__content__block">
        <div className="text--stamp">{getContextBasedHeadline(lang, context as ISignupContext)}</div>
        <h1>{translations.loginUsingCpr}</h1>
        <form
          className="form"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className={errors.disclaimerCheck ? 'form__field form__field--horizontal form-info--error' : 'form__field form__field--horizontal'}>
            <label htmlFor="disclaimerCheck">
              {disclaimer}
              {errors.disclaimerCheck
                && (
                  <span className="form-info__message">
                    {errors.disclaimerCheck.message}
                  </span>
                )}
            </label>
            <input
              id="disclaimerCheck"
              type="checkbox"
              {...register('disclaimerCheck', {
                validate: (x) => {
                  if (x === false) {
                    return translations.mustAcceptDisclaimer;
                  }
                  return true;
                },
              })}
              disabled={uiBusy}
            />
          </div>
          <div className="submit button-container">
            <button type="submit" className={uiBusy ? 'button button--processing' : 'button'} disabled={uiBusy || error != null}>{translations.next}</button>
          </div>
        </form>
      </div>
    );
  }

  let buttonClass = 'button';
  if (uiBusy) {
    buttonClass = `${buttonClass} button--processing`;
  }

  if (error != null) {
    buttonClass = `${buttonClass} visually-disabled`;
  }

  return (
    <AUSpinnerComponent
      loaded={!uiBusy}
    >
      <>

        {userSignupByCprResult && userSignupByCprResult.result === 'PrivateEmailNeeded' && (
          <div className="page__content__block">
            <h1>{translations.loginUsingCpr}</h1>
            <form className="form" noValidate onSubmit={handleSubmit(onSubmit)}>
              <div className={errors.privateEmail ? 'form__field form-info--error' : 'form__field'}>
                <label htmlFor="privateEmail">
                  {translations.privateEmail}
                </label>
                {errors.privateEmail
                  && (
                    <span className="form-info__message">
                      {errors.privateEmail.message}
                    </span>
                  )}
                <input
                  type="text"
                  id="privateEmail"
                  {...register('privateEmail', {
                    required: {
                      value: true,
                      message: translations.privateEmailIsRequired,
                    },
                    pattern: {
                      value: emailRegex,
                      message: translations.invalidEmail,
                    },
                  })}
                  disabled={uiBusy}
                />
              </div>
              <div className="submit button-container">
                <button type="submit" className={buttonClass} disabled={uiBusy}>{translations.next}</button>
              </div>
            </form>
          </div>
        )}
        {
          (userSignupByCprResult && userSignupByCprResult.result !== 'PrivateEmailNeeded'
            && userSignupByCprResult.result !== 'Error') && (
            <ReceiptComponent
              confirmDetails={userSignupByCprResult}
            />
          )
        }
        {
          invalidCpr && (
            <div className="page__content__block">
              <h1>{translations.loginUsingCpr}</h1>
              <ErrorComponent
                header={translations.invalidCpr}
                text={translations.invalidCprText}
              />
              <div className="vertical-spacing-top--large">
                <a className="button" href={`/${lang}/create-account/?context=${key}`}>{translations.back}</a>
              </div>
            </div>
          )
        }
        {
          error && !invalidCpr && (
            <div className="page__content__block">
              <ErrorComponent
                header={translations.errorHeader}
                text={replace(translations.errorBody, ['context'], [key])}
              />
            </div>
          )
        }
      </>
    </AUSpinnerComponent>
  );
};

CreateCPRComponent.displayName = 'CreateCPRComponent';
export default CreateCPRComponent;
